import type { AlvoStatus, StatusAlvo } from '../../api/monitoramento';

/**
 * Agrupamento por "experiência do usuário" — responde "tem algo fora?" em
 * 6 cards (5 alvos externos + 1 pipeline interna), em vez de 90+ alvos
 * individuais. Cada grupo agrega N alvos físicos e exibe o pior status
 * entre eles. Drill-in revela os alvos individuais com tráfego recente.
 *
 * <p>Mapping por prefixo de alvo:
 * <ul>
 *   <li>{@code pdpj.sso}        → Login SSO</li>
 *   <li>{@code pdpj.portal}     → Peticionamento via Portal</li>
 *   <li>{@code pdpj.datalake}   → Consulta via Data Lake</li>
 *   <li>{@code tribunal.pje.*}  → Consulta direto PJe</li>
 *   <li>(reservado)             → Peticionamento direto PJe (vazio hoje;
 *       será populado quando o fallback PJe for implementado — fica
 *       SEM_DADOS até lá, com card cinza explícito)</li>
 * </ul>
 *
 * <p>O 6º card "Pipeline interna" não consome AlvoStatus: vem do resumo
 * agregado das 3 filas de job (ver {@code useResumoJobs}).
 */

export type ExperienceGroupId =
  | 'login_sso'
  | 'peticionar_portal'
  | 'peticionar_pje'
  | 'consultar_datalake'
  | 'consultar_pje'
  | 'pipeline_interna';

export interface ExperienceGroupConfig {
  id: ExperienceGroupId;
  label: string;
  descricaoCurta: string;
  /** Alvos físicos só são listados no drill — aqui guardamos como derivar. */
  pertence: (alvo: string) => boolean;
}

export const EXPERIENCE_GROUPS: ExperienceGroupConfig[] = [
  {
    id: 'login_sso',
    label: 'Login SSO',
    descricaoCurta: 'Autenticação na PDPJ via SSO/CNJ',
    pertence: (alvo) => alvo === 'pdpj.sso',
  },
  {
    id: 'peticionar_portal',
    label: 'Peticionamento via Portal',
    descricaoCurta: 'Protocolo via Portal de Serviços do CNJ',
    pertence: (alvo) => alvo === 'pdpj.portal',
  },
  {
    id: 'consultar_datalake',
    label: 'Consulta via Data Lake',
    descricaoCurta: 'Carga de processos via Data Lake do CNJ',
    pertence: (alvo) => alvo === 'pdpj.datalake',
  },
  {
    id: 'consultar_pje',
    label: 'Consulta direto PJe',
    descricaoCurta: 'Acesso direto ao PJe de cada tribunal',
    pertence: (alvo) => alvo.startsWith('tribunal.pje.'),
  },
  {
    id: 'peticionar_pje',
    label: 'Peticionamento direto PJe',
    // Sem call sites hoje — fica SEM_DADOS até o fallback ser implementado.
    descricaoCurta: 'Fallback direto no PJe (não implementado)',
    pertence: () => false,
  },
  // pipeline_interna é especial: não consome alvos, vem do resumo de jobs.
  // Inclui aqui apenas para a UI listar 6 cards uniformemente.
  {
    id: 'pipeline_interna',
    label: 'Pipeline interna',
    descricaoCurta: 'Saúde das três filas de job do autenticador',
    pertence: () => false,
  },
];

/**
 * Devolve os alvos de um grupo. Para `pipeline_interna` retorna [] — a UI
 * trata esse grupo separadamente, consumindo `useResumoJobs`.
 */
export function alvosDoGrupo(grupoId: ExperienceGroupId, alvos: AlvoStatus[]): AlvoStatus[] {
  const cfg = EXPERIENCE_GROUPS.find((g) => g.id === grupoId);
  if (!cfg) return [];
  return alvos.filter((a) => cfg.pertence(a.alvo));
}

/**
 * Status agregado do grupo = pior status entre os alvos com tráfego.
 * Hierarquia: INDISPONIVEL > DEGRADADO > ATIVO > SEM_DADOS. Grupo sem
 * nenhum alvo conhecido → SEM_DADOS (cinza, "sem chamadas registradas").
 */
export function statusAgregado(alvos: AlvoStatus[]): StatusAlvo {
  if (alvos.length === 0) return 'SEM_DADOS';
  if (alvos.some((a) => a.status === 'INDISPONIVEL')) return 'INDISPONIVEL';
  if (alvos.some((a) => a.status === 'DEGRADADO')) return 'DEGRADADO';
  if (alvos.every((a) => a.status === 'SEM_DADOS')) return 'SEM_DADOS';
  return 'ATIVO';
}

export function totalAlvos(alvos: AlvoStatus[]): number {
  return alvos.length;
}

export function alvosComProblema(alvos: AlvoStatus[]): number {
  return alvos.filter((a) => a.status === 'DEGRADADO' || a.status === 'INDISPONIVEL').length;
}
